import { BUSINESS_TYPE_LABELS, BUSINESS_TYPES, createLocalSchema, slugify } from '@gl/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { Link, useNavigate } from 'react-router';
import { FormField } from '@/components/FormField';
import { Spinner } from '@/components/FullScreenLoader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AuthLayout } from '@/features/auth/AuthLayout';
import { api, errorMessage, PUBLIC_MENU_BASE_URL } from '@/lib/api';
import { queryKeys } from '@/lib/query';
import { WELCOME_PARAM } from '@/features/resumen/ResumenPage';

const COLORS = ['#DB2777', '#E11D48', '#EA580C', '#CA8A04', '#16A34A', '#0891B2', '#2563EB', '#7C3AED', '#171717'];

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

/** `/onboarding`: crear un local (nombre, slug auto editable y validado, rubro, color). */
export function OnboardingPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [slugTouched, setSlugTouched] = useState(false);

  const form = useForm({
    resolver: zodResolver(createLocalSchema),
    defaultValues: { name: '', slug: '', businessType: 'HELADERIA' as const, primaryColor: COLORS[0] },
  });
  const { errors } = form.formState;
  const name = useWatch({ control: form.control, name: 'name' });
  const slug = useWatch({ control: form.control, name: 'slug' });
  const color = useWatch({ control: form.control, name: 'primaryColor' });

  // Slug automático a partir del nombre hasta que el usuario lo edite.
  useEffect(() => {
    if (!slugTouched) form.setValue('slug', slugify(name ?? ''), { shouldValidate: !!name });
  }, [name, slugTouched, form]);

  const debouncedSlug = useDebounced(slug ?? '', 350);
  const slugCheck = useQuery({
    queryKey: queryKeys.checkSlug(debouncedSlug),
    queryFn: () => api.locals.checkSlug(debouncedSlug),
    enabled: debouncedSlug.length >= 3,
    staleTime: 10_000,
  });

  const create = useMutation({
    mutationFn: api.locals.create,
    onSuccess: async (local) => {
      queryClient.setQueryData(queryKeys.local(local.id), local);
      await queryClient.invalidateQueries({ queryKey: queryKeys.locals });
      // Resumen muestra la checklist de primeros pasos con la bienvenida.
      navigate(`/l/${local.id}/resumen?${WELCOME_PARAM}=1`, { replace: true });
    },
  });

  const slugStatus =
    debouncedSlug !== slug || slugCheck.isFetching
      ? 'checking'
      : slugCheck.data
        ? slugCheck.data.available
          ? 'ok'
          : slugCheck.data.reason
        : null;
  const slugHint =
    slugStatus === 'TAKEN'
      ? 'Ese nombre ya lo usa otro local'
      : slugStatus === 'RESERVED'
        ? 'Ese nombre está reservado'
        : `Tu menú va a quedar en ${PUBLIC_MENU_BASE_URL.replace(/^https?:\/\//, '')}/${slug || 'tu-local'}`;

  return (
    <AuthLayout
      title="Creá tu local"
      subtitle="Después te guiamos para cargar el menú y publicarlo. Todo se puede cambiar desde Configuración."
      footer={
        <Link to="/" className="hover:underline">
          Volver
        </Link>
      }
    >
      <form className="grid gap-4" onSubmit={form.handleSubmit((v) => create.mutate(v))} noValidate>
        <FormField label="Nombre del local" htmlFor="name" error={errors.name?.message}>
          <Input id="name" placeholder="Heladería La Esquina" aria-invalid={!!errors.name} {...form.register('name')} />
        </FormField>

        <FormField label="Dirección del menú" htmlFor="slug" error={errors.slug?.message} hint={slugHint}>
          <div className="relative">
            <Input
              id="slug"
              aria-invalid={!!errors.slug || slugStatus === 'TAKEN' || slugStatus === 'RESERVED'}
              {...form.register('slug', {
                onChange: () => setSlugTouched(true),
              })}
              className="pr-9"
              autoCapitalize="none"
              autoCorrect="off"
            />
            <span className="absolute top-1/2 right-3 -translate-y-1/2">
              {slugStatus === 'checking' && debouncedSlug.length >= 3 && <Spinner className="size-4 text-muted-foreground" />}
              {slugStatus === 'ok' && <Check className="size-4 text-success" />}
              {(slugStatus === 'TAKEN' || slugStatus === 'RESERVED' || slugStatus === 'INVALID') && (
                <X className="size-4 text-destructive" />
              )}
            </span>
          </div>
        </FormField>

        <FormField label="Rubro" error={errors.businessType?.message}>
          <Controller
            control={form.control}
            name="businessType"
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {BUSINESS_TYPES.map((t) => (
                    <SelectItem key={t} value={t}>
                      {BUSINESS_TYPE_LABELS[t]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </FormField>

        <FormField label="Color de tu marca" error={errors.primaryColor?.message}>
          <div className="flex flex-wrap items-center gap-2">
            {COLORS.map((c) => (
              <button
                key={c}
                type="button"
                aria-label={`Color ${c}`}
                onClick={() => form.setValue('primaryColor', c)}
                className="size-8 rounded-full ring-offset-2 ring-offset-background transition data-[active=true]:ring-2 data-[active=true]:ring-foreground"
                style={{ backgroundColor: c }}
                data-active={color?.toUpperCase() === c}
              />
            ))}
            <input
              type="color"
              aria-label="Otro color"
              value={color ?? COLORS[0]}
              onChange={(e) => form.setValue('primaryColor', e.target.value.toUpperCase())}
              className="size-8 cursor-pointer rounded-full border bg-transparent p-0"
            />
          </div>
        </FormField>

        {create.error && <p className="text-sm text-destructive">{errorMessage(create.error)}</p>}
        <Button
          type="submit"
          size="lg"
          disabled={create.isPending || slugStatus === 'TAKEN' || slugStatus === 'RESERVED'}
        >
          {create.isPending && <Spinner />} Crear local
        </Button>
      </form>
    </AuthLayout>
  );
}
